import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'system';
  timestamp: Date;
  status?: 'pending' | 'success' | 'error';
  taskId?: string; // 关联的任务ID
}

interface DownloadTask {
  id: string;
  videoId: string;
  status: 'processing' | 'completed' | 'failed';
  title?: string;
}

const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      text: '👋 欢迎使用 Vdio 助手！\n\n请发送抖音视频链接，我会自动下载并保存到您的视频库中。\n\n支持格式：\n• https://v.douyin.com/xxx/\n• 抖音App复制的完整文本\n\n💡 **支持多任务**：可以连续发送多个链接，它们会同时并行处理！',
      sender: 'system',
      timestamp: new Date(),
    }
  ]);
  const [inputText, setInputText] = useState('');
  const [activeTasks, setActiveTasks] = useState<Map<string, DownloadTask>>(new Map());
  const [isFullScreen, setIsFullScreen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 监听浏览器全屏状态变化
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // 切换全屏
  const toggleFullScreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await containerRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('全屏切换失败:', err);
    }
  };

  // 自动滚动到底部
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const isValidDouyinLink = (text: string): boolean => {
    const douyinPatterns = [
      /https?:\/\/.*douyin\.com\/video\//,
      /https?:\/\/.*douyin\.com\/note\//,
      /https?:\/\/v\.douyin\.com\//,
      /https?:\/\/www\.iesdouyin\.com\//
    ];
    return douyinPatterns.some(pattern => pattern.test(text));
  };

  // 从文本中提取所有抖音链接
  const extractDouyinUrls = (text: string): string[] => {
    const urls: string[] = [];
    const lines = text.split('\n');
    
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      
      // 尝试从行中提取URL
      const urlMatch = trimmed.match(/https?:\/\/[^\s]+/);
      if (urlMatch) {
        const url = urlMatch[0];
        if (isValidDouyinLink(url)) {
          urls.push(url);
        }
      }
    }
    
    return urls;
  };

  // 兼容旧版：提取单个链接
  const extractDouyinUrl = (text: string): string => {
    const urls = extractDouyinUrls(text);
    return urls.length > 0 ? urls[0] : '';
  };

  const addMessage = (text: string, sender: 'user' | 'system', status?: 'pending' | 'success' | 'error') => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      sender,
      timestamp: new Date(),
      status
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const handleSendMessage = () => {
    if (!inputText.trim()) return;

    const userMessage = inputText.trim();
    setInputText('');
    addMessage(userMessage, 'user');

    // 检查是否为抖音链接（支持批量）
    const urls = extractDouyinUrls(userMessage);
    
    if (urls.length === 0) {
      addMessage('❌ 未检测到有效的抖音链接。\n\n请发送抖音视频链接或包含链接的完整复制文本。', 'system', 'error');
      return;
    }
    
    // 如果只有一个链接，使用原有单任务处理
    if (urls.length === 1) {
      const taskId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const processingMsgId = `msg-${taskId}`;
      
      setMessages(prev => [...prev, {
        id: processingMsgId,
        text: '🔄 正在处理抖音链接...',
        sender: 'system',
        timestamp: new Date(),
        status: 'pending',
        taskId: taskId
      }]);
      
      setActiveTasks(prev => new Map(prev.set(taskId, {
        id: taskId,
        videoId: '',
        status: 'processing'
      })));

      processDownloadTask(taskId, processingMsgId, urls[0]);
      return;
    }
    
    // 批量处理多个链接
    const batchTaskId = `batch-${Date.now()}`;
    const batchMsgId = `msg-${batchTaskId}`;
    
    setMessages(prev => [...prev, {
      id: batchMsgId,
      text: `📥 批量处理 ${urls.length} 个抖音链接...`,
      sender: 'system',
      timestamp: new Date(),
      status: 'pending',
      taskId: batchTaskId
    }]);
    
    // 调用批量下载API
    processBatchDownload(batchMsgId, urls);
  };
  
  // 批量下载处理
  const processBatchDownload = async (msgId: string, urls: string[]) => {
    try {
      const response = await axios.post('/api/videos/download/batch', { urls });
      const result = response.data;
      
      let detailText = '';
      if (result.results && result.results.length > 0) {
        detailText = '\n\n' + result.results.map((item: any) => {
          const icon = item.status === 'queued' ? '✅' : item.status === 'exists' ? '🔄' : '❌';
          const title = item.title || item.url.substring(0, 30) + '...';
          return `${icon} ${title}`;
        }).join('\n');
      }
      
      setMessages(prev => prev.map(msg => 
        msg.id === msgId 
          ? { 
              ...msg, 
              text: `✅ 批量处理完成！\n成功: ${result.success} | 已存在: ${result.duplicate} | 失败: ${result.failed}${detailText}`, 
              status: 'success' 
            }
          : msg
      ));
    } catch (error: any) {
      setMessages(prev => prev.map(msg => 
        msg.id === msgId 
          ? { ...msg, text: `❌ 批量处理失败: ${error.message || '未知错误'}`, status: 'error' }
          : msg
      ));
    }
  };

  // 独立处理下载任务（非阻塞，带自动重试）
  const processDownloadTask = async (
    taskId: string, 
    processingMsgId: string, 
    cleanUrl: string,
    retryCount: number = 0,
    maxRetries: number = 3
  ) => {
    try {
      // 更新消息显示重试状态
      if (retryCount > 0) {
        setMessages(prev => prev.map(msg => 
          msg.id === processingMsgId 
            ? { ...msg, text: `🔄 第 ${retryCount}/${maxRetries} 次重试中...\n链接: ${cleanUrl}`, status: 'pending' }
            : msg
        ));
      }

      // 调用后端API处理抖音链接（不设置超时，让大文件下载可以持续）
      const response = await axios.post('/api/videos/download', {
        url: cleanUrl
      }, {
        // 不设置timeout，使用浏览器默认超时或无限制
        // 大文件下载可能需要很长时间
      });

      if (response.status === 200) {
        const result = response.data;
        
        // 处理去重：视频已存在
        if (result.status === 'exists') {
          setMessages(prev => prev.map(msg => 
            msg.id === processingMsgId 
              ? { ...msg, text: `🔄 视频已存在，跳过下载\n${result.title || '抖音视频'}`, status: 'success' }
              : msg
          ));
          setActiveTasks(prev => {
            const newMap = new Map(prev);
            newMap.delete(taskId);
            return newMap;
          });
          return;
        }
        
        // 更新任务信息
        setActiveTasks(prev => {
          const newMap = new Map(prev);
          const task = newMap.get(taskId);
          if (task) {
            task.videoId = result.id;
            task.title = result.title;
          }
          return newMap;
        });
        
        // 处理队列状态
        const isQueued = result.status === 'queued' && result.queuePosition > 1;
        
        // 更新处理中消息
        setMessages(prev => prev.map(msg => 
          msg.id === processingMsgId 
            ? { ...msg, text: isQueued 
                ? `📥 视频已加入下载队列\n位置: ${result.queuePosition}/${result.queueLength} | ${result.title || '抖音视频'}`
                : `✅ 视频解析成功！\n开始下载: ${result.title || '抖音视频'}`, 
                status: 'success' }
            : msg
        ));

        // 轮询检查下载状态（带进度显示）
        let lastProgressText = '';
        const checkStatus = async () => {
          try {
            const statusResponse = await axios.get(`/api/videos/download/${result.id}/status`);
            if (statusResponse.status === 200) {
              const statusData = statusResponse.data;
              if (statusData.status === 'completed') {
                addMessage(`🎉 "${result.title || '视频'}" 下载完成！\n已保存到您的视频库中。`, 'system', 'success');
                // 从活跃任务中移除
                setActiveTasks(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(taskId);
                  return newMap;
                });
              } else if (statusData.status === 'failed') {
                addMessage(`❌ "${result.title || '视频'}" 下载失败: ${statusData.message || '未知错误'}`, 'system', 'error');
                setActiveTasks(prev => {
                  const newMap = new Map(prev);
                  newMap.delete(taskId);
                  return newMap;
                });
              } else {
                // 下载中，显示进度
                if (statusData.progress !== undefined || statusData.percent !== undefined) {
                  // 调试信息
                  console.log('📊 收到进度数据:', statusData);
                  
                  const progress = statusData.progress ?? statusData.percent ?? 0;
                  const downloaded = statusData.downloaded ?? 0;
                  const total = statusData.total;
                  const speed = statusData.speed ?? 0;
                  
                  let progressText;
                  if (total && total > 0) {
                    progressText = `⏳ 正在下载: ${result.title || '抖音视频'}\n📊 ${progress.toFixed(1)}% | ${downloaded.toFixed(1)}/${total.toFixed(1)} MB | ${speed.toFixed(2)} MB/s`;
                  } else {
                    // 文件大小未知时，只显示已下载量和速度
                    progressText = `⏳ 正在下载: ${result.title || '抖音视频'}\n📊 已下载: ${downloaded.toFixed(1)} MB | ${speed.toFixed(2)} MB/s`;
                  }
                  
                  // 只有进度变化时才更新UI，减少重渲染
                  if (progressText !== lastProgressText) {
                    lastProgressText = progressText;
                    setMessages(prev => prev.map(msg => 
                      msg.id === processingMsgId 
                        ? { ...msg, text: progressText, status: 'pending' }
                        : msg
                    ));
                  }
                } else {
                  // 没有进度信息，显示等待中或队列状态
                  let progressText;
                  if (isQueued && !statusData.progress) {
                    // 仍在队列中等待
                    progressText = `📥 排队等待下载: ${result.title || '抖音视频'}\n📊 请稍候，正在处理队列中的其他视频...`;
                  } else {
                    progressText = `⏳ 正在下载: ${result.title || '抖音视频'}\n📊 等待服务器响应...`;
                  }
                  if (progressText !== lastProgressText) {
                    lastProgressText = progressText;
                    setMessages(prev => prev.map(msg => 
                      msg.id === processingMsgId 
                        ? { ...msg, text: progressText, status: 'pending' }
                        : msg
                    ));
                  }
                }
                // 继续轮询
                setTimeout(checkStatus, 1500);
              }
            } else {
              addMessage('❌ 检查下载状态失败，请稍后重试。', 'system', 'error');
              setActiveTasks(prev => {
                const newMap = new Map(prev);
                newMap.delete(taskId);
                return newMap;
              });
            }
          } catch (error) {
            addMessage('❌ 下载状态检查出错，请稍后重试。', 'system', 'error');
            setActiveTasks(prev => {
              const newMap = new Map(prev);
              newMap.delete(taskId);
              return newMap;
            });
          }
        };

        // 开始轮询
        setTimeout(checkStatus, 2000);
        
      } else {
        addMessage('❌ 视频处理失败，请检查链接是否有效。', 'system', 'error');
        setActiveTasks(prev => {
          const newMap = new Map(prev);
          newMap.delete(taskId);
          return newMap;
        });
      }
    } catch (error: any) {
      console.error('❌ 处理抖音链接失败:', error);
      
      // 判断是否可重试（500错误或网络超时）
      const isRetryable = 
        (error.response && error.response.status === 500) ||
        error.code === 'ECONNABORTED' ||
        !error.response;
      
      // 如果还可以重试，则自动重试
      if (isRetryable && retryCount < maxRetries) {
        console.log(`🔄 自动重试 (${retryCount + 1}/${maxRetries})...`);
        
        // 延迟1秒后重试
        setTimeout(() => {
          processDownloadTask(taskId, processingMsgId, cleanUrl, retryCount + 1, maxRetries);
        }, 1000 + retryCount * 1000); // 递增延迟：1s, 2s, 3s
        
        return; // 不删除任务，等待重试
      }
      
      // 重试次数用完或不可重试，显示最终错误
      let errorMessage = '❌ 处理抖音链接时发生错误。';
      
      if (error.response) {
        console.error('服务器错误响应:', error.response.data);
        if (error.response.status === 400) {
          errorMessage = '❌ 请提供有效的抖音视频链接。';
        } else if (error.response.status === 500) {
          // 显示服务器返回的详细错误信息
          const serverError = error.response.data;
          errorMessage = `❌ 服务器处理失败（已重试${retryCount}次）\n${serverError.message || '未知错误'}\n\n${serverError.stack || ''}`.trim();
        }
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = `❌ 请求超时（已重试${retryCount}次），请检查网络连接。`;
      }
      
      // 更新处理中消息为错误
      setMessages(prev => prev.map(msg => 
        msg.id === processingMsgId 
          ? { ...msg, text: errorMessage, status: 'error' }
          : msg
      ));
      
      setActiveTasks(prev => {
        const newMap = new Map(prev);
        newMap.delete(taskId);
        return newMap;
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div ref={containerRef} className={`flex flex-col bg-black text-white transition-all duration-300 ${isFullScreen ? 'fixed inset-0 z-50' : 'h-screen'}`}>
      {/* 聊天标题 */}
      <div className="bg-gray-900 text-white p-4 shadow-md">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">Vdio 助手</h1>
            <p className="text-gray-400 text-sm">发送抖音链接，自动下载保存</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* 全屏切换按钮 */}
            <button
              onClick={toggleFullScreen}
              className="px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm transition-colors flex items-center space-x-1"
              title={isFullScreen ? '退出全屏' : '全屏'}
            >
              <span>{isFullScreen ? '⛶' : '⛶'}</span>
              <span>{isFullScreen ? '退出' : '全屏'}</span>
            </button>
            {/* 活跃任务指示器 */}
            {activeTasks.size > 0 && (
              <div className="flex items-center space-x-2 bg-blue-600/20 px-3 py-1.5 rounded-full">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-blue-400">
                  {activeTasks.size} 个任务处理中
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 消息区域 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xs md:max-w-md lg:max-w-lg px-4 py-2 rounded-lg ${
                message.sender === 'user'
                  ? 'bg-blue-600 text-white'
                  : message.status === 'error'
                  ? 'bg-red-900/50 text-red-300'
                  : message.status === 'pending'
                  ? 'bg-yellow-900/50 text-yellow-300'
                  : 'bg-gray-800 text-gray-200'
              }`}
            >
              <div className="whitespace-pre-wrap break-words">{message.text}</div>
              <div className="text-xs opacity-70 mt-1 text-right">
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* 输入区域 */}
      <div className="border-t border-gray-700 p-4 bg-gray-900">
        <div className="flex space-x-2">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="发送抖音视频链接..."
            className="flex-1 border border-gray-700 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-gray-800 text-white"
            rows={1}

            style={{ minHeight: '44px', maxHeight: '120px' }}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputText.trim()}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              !inputText.trim()
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {activeTasks.size > 0 ? `发送 (${activeTasks.size}个任务)` : '发送'}
          </button>
        </div>
        <div className="text-xs text-gray-500 mt-2 text-center">
          💡 提示：可以直接粘贴抖音App中的完整复制文本
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;